import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Heading,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  VStack,
  Flex,
  useToast,
  SimpleGrid,
  FormHelperText,
  Alert,
  AlertIcon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Badge,
  Spinner,
  Center,
  IconButton,
  Wrap,
  WrapItem,
  Grid,
  GridItem,
  Tag,
} from '@chakra-ui/react';
import { format, addDays, isBefore, isAfter, eachDayOfInterval } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CheckboxGroup, Checkbox } from '@chakra-ui/react';
import { AddIcon, DeleteIcon, SmallCloseIcon } from '@chakra-ui/icons';
import AppLayout from '../components/layout/AppLayout';
import { useAuthStore } from '../store/authStore';
import { useOptimizedUserStore as useUserStore } from '../store/optimizedUserStore';
import { formatISODate, parseISODatePreserved, normalizeDate, isSameDayNormalized } from '../utils/dateUtils';
import DatePickerInput from '../components/common/DatePickerInput';

// Import additional styling for the date picker
import '../styles/datepicker-overrides.css';

// Type for vacation date range
interface VacationRange {
  startDate: Date;
  endDate: Date;
  id: string; // Unique identifier for each range
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, loading: authLoading } = useAuthStore();
  const {
    profile,
    vacationDays,
    profileLoading,
    fetchUserProfile,
    fetchVacationDays,
    updateProfile,
    batchUpdateVacationDays,
  } = useUserStore();

  // Settings state
  const [yearlyTarget, setYearlyTarget] = useState(1800);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [workWeekDays, setWorkWeekDays] = useState<number[]>([]);
  const [selectedVacationDays, setSelectedVacationDays] = useState<Date[]>([]);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  
  // Date range picker state
  const [newRangeStart, setNewRangeStart] = useState<Date>(new Date());
  const [newRangeEnd, setNewRangeEnd] = useState<Date>(new Date());
  const [vacationRanges, setVacationRanges] = useState<VacationRange[]>([]);

  // For the vacation days modal
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Fetch and initialize data once when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (authLoading || initialDataLoaded || !user) return;
      
      try {
        console.log('Loading settings page data...');
        setPageLoading(true);
        
        // Load profile first
        await fetchUserProfile();
        
        // Then load vacation days
        await fetchVacationDays();
        
        setInitialDataLoaded(true);
      } catch (err) {
        console.error('Error loading settings data:', err);
      } finally {
        setPageLoading(false);
      }
    };

    if (user && !initialDataLoaded) {
      loadData();
    } else if (!authLoading && !user) {
      // Redirect to login if not authenticated
      navigate('/login');
    }
  }, [user, authLoading, fetchUserProfile, fetchVacationDays, initialDataLoaded, navigate]);

  // Update local state when profile is loaded - only do this once
  useEffect(() => {
    if (profile && !profileLoading) {
      setYearlyTarget(profile.yearly_target_hours);
      
      // Use parseISODatePreserved to correctly handle the date conversion
      setStartDate(parseISODatePreserved(profile.tracking_start_date));
      setEndDate(parseISODatePreserved(profile.tracking_end_date));
      
      setWorkWeekDays(profile.work_week_days);
      
      // Done loading
      setPageLoading(false);
    }
  }, [profile, profileLoading]);

  // Load vacation days - only do this once when vacation days are loaded
  useEffect(() => {
    if (vacationDays.length > 0) {
      // Use parseISODatePreserved to correctly parse dates from DB
      const dates = vacationDays.map((vd) => parseISODatePreserved(vd.vacation_date));
      setSelectedVacationDays(dates);
      
      // Convert individual dates to potential ranges
      identifyDateRanges(dates);
    }
  }, [vacationDays]);
  
  // Convert individual dates to potential date ranges
  const identifyDateRanges = (dates: Date[]) => {
    if (dates.length === 0) {
      setVacationRanges([]);
      return;
    }
    
    // Sort dates chronologically
    const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
    
    const ranges: VacationRange[] = [];
    let currentRange: { startDate: Date; endDate: Date } | null = null;
    
    // Iterate through sorted dates to identify consecutive ranges
    sortedDates.forEach((date, index) => {
      if (index === 0) {
        // First date - start a new range
        currentRange = { startDate: date, endDate: date };
      } else {
        const prevDate = sortedDates[index - 1];
        const dayDiff = Math.round((date.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (dayDiff === 1) {
          // Consecutive day - extend current range
          if (currentRange) {
            currentRange.endDate = date;
          }
        } else {
          // Non-consecutive day - save current range and start a new one
          if (currentRange) {
            ranges.push({
              ...currentRange,
              id: `range-${ranges.length}-${currentRange.startDate.getTime()}`,
            });
          }
          currentRange = { startDate: date, endDate: date };
        }
      }
      
      // If we're at the last date, add the current range
      if (index === sortedDates.length - 1 && currentRange) {
        ranges.push({
          ...currentRange,
          id: `range-${ranges.length}-${currentRange.startDate.getTime()}`,
        });
      }
    });
    
    setVacationRanges(ranges);
  };

  // Redirect to setup if needed, but only if we've actually tried loading the profile
  useEffect(() => {
    if (!authLoading && !profileLoading && initialDataLoaded && user && profile === null) {
      console.log('No profile found, redirecting to setup wizard');
      navigate('/setup');
    }
  }, [authLoading, profileLoading, initialDataLoaded, profile, navigate, user]);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      // Format dates to strings using our improved formatter
      const formattedStartDate = formatISODate(startDate);
      const formattedEndDate = formatISODate(endDate);

      await updateProfile({
        yearly_target_hours: yearlyTarget,
        tracking_start_date: formattedStartDate,
        tracking_end_date: formattedEndDate,
        work_week_days: workWeekDays,
        setup_completed: true, // Ensure setup_completed is always true when saving settings
      });
      
      // Don't immediately navigate away - we want to do additional steps
      setSuccessMessage('Settings updated successfully');
      toast({
        title: 'Settings updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Force refresh the target summary with the new profile settings
      // This ensures the projected completion date gets recalculated immediately
      try {
        const { refreshTargetSummary } = useUserStore.getState();
        await refreshTargetSummary(false);
        console.log('Target summary refreshed with new profile settings');
      } catch (summaryError) {
        console.error('Error refreshing target summary after profile update:', summaryError);
      }
      
      // Now navigate to dashboard with the updated data
      setSaving(false);
      navigate('/');
    } catch (error) {
      console.error('Error saving settings:', error);
      setErrorMessage('Failed to update settings');
      setSaving(false);
    }
  };

  const handleAddVacationDays = async (dates: Date[]) => {
    try {
      setSaving(true);
      setErrorMessage(null);
      
      // Convert to ISO strings using our improved formatter
      const isoStrings = dates.map(date => formatISODate(date));
      
      // Use the new batch update function instead of individual operations
      await batchUpdateVacationDays(isoStrings);
      
      toast({
        title: 'Vacation days updated',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setSaving(false);
      onClose();
    } catch (error) {
      console.error('Error updating vacation days:', error);
      setErrorMessage('Failed to update vacation days');
      setSaving(false);
    }
  };
  
  // Add a date range to selected vacation days
  const handleAddDateRange = () => {
    // Normalize dates first
    const normalizedStart = normalizeDate(newRangeStart);
    const normalizedEnd = normalizeDate(newRangeEnd);
    
    // Ensure start date is before end date
    const actualStartDate = isBefore(normalizedStart, normalizedEnd) ? normalizedStart : normalizedEnd;
    const actualEndDate = isAfter(normalizedEnd, normalizedStart) ? normalizedEnd : normalizedStart;
    
    // Generate all dates in the range
    const datesInRange = eachDayOfInterval({ start: actualStartDate, end: actualEndDate });
    
    // Filter out dates that are already selected to avoid duplicates
    const newDates = datesInRange.filter(
      newDate => !selectedVacationDays.some(existingDate => 
        isSameDayNormalized(existingDate, newDate)
      )
    );
    
    // Add to selected days
    const updatedDays = [...selectedVacationDays, ...newDates];
    setSelectedVacationDays(updatedDays);
    
    // Add to ranges
    setVacationRanges([
      ...vacationRanges,
      {
        startDate: actualStartDate,
        endDate: actualEndDate,
        id: `range-${vacationRanges.length}-${actualStartDate.getTime()}`
      }
    ]);
    
    // Reset the date picker to prepare for next range
    const tomorrow = normalizeDate(addDays(new Date(), 1));
    setNewRangeStart(tomorrow);
    setNewRangeEnd(tomorrow);
  };
  
  // Remove a range of dates
  const handleRemoveRange = (rangeId: string) => {
    // Find the range to remove
    const rangeToRemove = vacationRanges.find(range => range.id === rangeId);
    
    if (rangeToRemove) {
      // Generate all dates in the range
      const datesToRemove = eachDayOfInterval({
        start: rangeToRemove.startDate,
        end: rangeToRemove.endDate
      });
      
      // Remove these dates from selectedVacationDays
      const updatedDays = selectedVacationDays.filter(selectedDate => 
        !datesToRemove.some(dateToRemove => isSameDayNormalized(dateToRemove, selectedDate))
      );
      
      setSelectedVacationDays(updatedDays);
      
      // Remove the range from vacationRanges
      setVacationRanges(vacationRanges.filter(range => range.id !== rangeId));
    }
  };
  
  // Handle removing a specific date
  const handleRemoveDate = (dateToRemove: Date) => {
    // Normalize the date we want to remove
    const normalizedDateToRemove = normalizeDate(dateToRemove);
    
    // Remove the date from selectedVacationDays using normalized comparison
    const updatedDays = selectedVacationDays.filter(
      date => !isSameDayNormalized(date, normalizedDateToRemove)
    );
    
    setSelectedVacationDays(updatedDays);
    
    // Recalculate ranges based on the updated days
    identifyDateRanges(updatedDays);
  };

  const handleWorkWeekChange = (values: string[]) => {
    const numericValues = values.map(val => parseInt(val, 10));
    setWorkWeekDays(numericValues);
  };
  
  // Display date in range format
  const formatDateRange = (startDate: Date, endDate: Date) => {
    if (isSameDayNormalized(startDate, endDate)) {
      return format(startDate, 'MMM d, yyyy');
    }
    
    if (startDate.getFullYear() === endDate.getFullYear()) {
      if (startDate.getMonth() === endDate.getMonth()) {
        // Same month and year, e.g., "May 15-20, 2025"
        return `${format(startDate, 'MMM d')}-${format(endDate, 'd, yyyy')}`;
      } else {
        // Same year but different months, e.g., "May 15 - Jun 2, 2025"
        return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
      }
    } else {
      // Different years, e.g., "Dec 28, 2024 - Jan 3, 2025"
      return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
    }
  };

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  if (pageLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <AppLayout>
      <Box mb={8}>
        <Heading as="h1" size="xl" mb={2}>
          Settings
        </Heading>
        <Text color="gray.600">
          Update your billable hours settings and preferences.
        </Text>
      </Box>

      {successMessage && (
        <Alert status="success" mb={6}>
          <AlertIcon />
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert status="error" mb={6}>
          <AlertIcon />
          {errorMessage}
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        <Card
          borderLeft="4px"
          borderColor="blue.500"
          boxShadow="md"
          transition="transform 0.2s"
          _hover={{ transform: 'translateY(-5px)' }}
        >
          <CardHeader bg="blue.50" borderBottomWidth="1px">
            <Heading size="md" color="blue.700">Billable Hours Target</Heading>
          </CardHeader>
          <CardBody>
            <FormControl mb={6}>
              <FormLabel>Annual Billable Hours Target</FormLabel>
              <NumberInput
                min={1}
                max={5000}
                step={10}
                value={yearlyTarget}
                onChange={(_, valueAsNumber) => setYearlyTarget(valueAsNumber)}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>
                Your yearly billable hours requirement
              </FormHelperText>
            </FormControl>
          </CardBody>
        </Card>

        <Card
          borderLeft="4px"
          borderColor="purple.500"
          boxShadow="md"
          transition="transform 0.2s"
          _hover={{ transform: 'translateY(-5px)' }}
          position="relative"
          zIndex={1}
        >
          <CardHeader bg="purple.50" borderBottomWidth="1px">
            <Heading size="md" color="purple.700">Tracking Period</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Start Date</FormLabel>
                <Box position="relative" className="date-picker-container">
                  <DatePicker
                    selected={startDate}
                    onChange={(date: Date | null) => date && setStartDate(date)}
                    customInput={<DatePickerInput />}
                    dateFormat="MM/dd/yyyy"
                    popperClassName="settings-date-popper"
                    popperPlacement="bottom-start"
                  />
                </Box>
                <FormHelperText>
                  First day of your tracking period
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>End Date</FormLabel>
                <Box position="relative" className="date-picker-container">
                  <DatePicker
                    selected={endDate}
                    onChange={(date: Date | null) => date && setEndDate(date)}
                    customInput={<DatePickerInput />}
                    dateFormat="MM/dd/yyyy"
                    minDate={startDate}
                    popperClassName="settings-date-popper"
                    popperPlacement="bottom-start"
                  />
                </Box>
                <FormHelperText>
                  Last day of your tracking period
                </FormHelperText>
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        <Card
          borderLeft="4px"
          borderColor="green.500"
          boxShadow="md"
          transition="transform 0.2s"
          _hover={{ transform: 'translateY(-5px)' }}
        >
          <CardHeader bg="green.50" borderBottomWidth="1px">
            <Heading size="md" color="green.700">Work Week</Heading>
          </CardHeader>
          <CardBody>
            <FormControl>
              <FormLabel>Select your work days</FormLabel>
              <CheckboxGroup
                colorScheme="green"
                value={workWeekDays.map(day => day.toString())}
                onChange={handleWorkWeekChange}
              >
                <VStack align="start" spacing={3}>
                  {daysOfWeek.map(day => (
                    <Checkbox key={day.value} value={day.value.toString()}>
                      {day.label}
                    </Checkbox>
                  ))}
                </VStack>
              </CheckboxGroup>
              <FormHelperText>
                Days of the week you typically work
              </FormHelperText>
            </FormControl>
          </CardBody>
        </Card>

        <Card
          borderLeft="4px"
          borderColor="teal.500"
          boxShadow="md"
          transition="transform 0.2s"
          _hover={{ transform: 'translateY(-5px)' }}
        >
          <CardHeader bg="teal.50" borderBottomWidth="1px">
            <Heading size="md" color="teal.700">Vacation Days</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Text>
                You have {selectedVacationDays.length} vacation days marked in your current tracking period.
              </Text>

              <Button colorScheme="teal" onClick={onOpen}>
                Manage Vacation Days
              </Button>

              <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Manage Vacation Days</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text mb={4}>
                      Select vacation days or date ranges during your tracking period.
                    </Text>
                    
                    {/* Date Range Selector */}
                    <Card mb={6} variant="outline">
                      <CardBody>
                        <Heading size="sm" mb={3}>Add Date Range</Heading>
                        <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                          <GridItem>
                            <FormControl>
                              <FormLabel fontSize="sm">Start Date</FormLabel>
                              <DatePicker
                                selected={newRangeStart}
                                onChange={(date: Date | null) => {
                                  if (date) {
                                    setNewRangeStart(date);
                                    // When start date changes, set the end date to match
                                    setNewRangeEnd(date);
                                  }
                                }}
                                customInput={<DatePickerInput size="sm" />}
                                dateFormat="MM/dd/yyyy"
                                minDate={startDate}
                                maxDate={endDate}
                              />
                            </FormControl>
                          </GridItem>
                          <GridItem>
                            <FormControl>
                              <FormLabel fontSize="sm">End Date</FormLabel>
                              <DatePicker
                                selected={newRangeEnd}
                                onChange={(date: Date | null) => date && setNewRangeEnd(date)}
                                customInput={<DatePickerInput size="sm" />}
                                dateFormat="MM/dd/yyyy"
                                minDate={startDate}
                                maxDate={endDate}
                              />
                            </FormControl>
                          </GridItem>
                        </Grid>
                        <Button 
                          colorScheme="green" 
                          size="sm" 
                          leftIcon={<AddIcon />}
                          onClick={handleAddDateRange}
                          width="full"
                        >
                          Add Date Range
                        </Button>
                      </CardBody>
                    </Card>
                    
                    {/* Selected Date Ranges */}
                    {vacationRanges.length > 0 && (
                      <Box mb={6}>
                        <Heading size="sm" mb={3}>Selected Date Ranges</Heading>
                        <VStack spacing={2} align="stretch">
                          {vacationRanges.map((range) => (
                            <Flex 
                              key={range.id}
                              justifyContent="space-between"
                              alignItems="center"
                              p={2}
                              borderWidth="1px"
                              borderRadius="md"
                              bg="gray.50"
                            >
                              <Text fontWeight="medium">
                                {formatDateRange(range.startDate, range.endDate)}
                                {" "}
                                <Badge colorScheme="purple" ml={2}>
                                  {Math.floor((range.endDate.getTime() - range.startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1} days
                                </Badge>
                              </Text>
                              <IconButton
                                aria-label="Remove date range"
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleRemoveRange(range.id)}
                              />
                            </Flex>
                          ))}
                        </VStack>
                      </Box>
                    )}
                    
                    {/* Individual Day Selection - Calendar View */}
                    <Box mb={6}>
                      <Heading size="sm" mb={3}>Selected Individual Days</Heading>
                      <Box>
                        <DatePicker
                          selected={selectedVacationDays.length > 0 ? selectedVacationDays[0] : startDate}
                          onChange={(date: Date | null) => {
                            if (date) {
                              // Normalize the date to avoid timezone issues
                              const normalizedDate = normalizeDate(date);
                              
                              // Toggle date selection using normalized comparison
                              if (selectedVacationDays.some(d => isSameDayNormalized(d, normalizedDate))) {
                                handleRemoveDate(normalizedDate);
                              } else {
                                setSelectedVacationDays([...selectedVacationDays, normalizedDate]);
                                // Recalculate ranges
                                identifyDateRanges([...selectedVacationDays, normalizedDate]);
                              }
                            }
                          }}
                          customInput={<DatePickerInput placeholder="Select vacation dates" />}
                          dateFormat="MM/dd/yyyy"
                          minDate={startDate}
                          maxDate={endDate}
                          highlightDates={selectedVacationDays}
                          inline
                        />
                      </Box>
                    </Box>

                    <Text fontWeight="medium" mb={2}>
                      Total Vacation Days: {selectedVacationDays.length}
                    </Text>
                    {selectedVacationDays.length > 0 && (
                      <Box maxH="150px" overflowY="auto" mb={4} borderWidth="1px" borderRadius="md" p={2}>
                        <Wrap spacing={2}>
                          {selectedVacationDays
                            .sort((a, b) => a.getTime() - b.getTime())
                            .map((date, index) => (
                              <WrapItem key={index}>
                                <Tag
                                  colorScheme="blue"
                                  borderRadius="full"
                                  size="md"
                                >
                                  {format(date, 'MMM d, yyyy')}
                                  <IconButton
                                    aria-label="Remove date"
                                    icon={<SmallCloseIcon />}
                                    size="xs"
                                    ml={1}
                                    colorScheme="blue"
                                    variant="ghost"
                                    onClick={() => handleRemoveDate(date)}
                                  />
                                </Tag>
                              </WrapItem>
                            ))}
                        </Wrap>
                      </Box>
                    )}
                  </ModalBody>

                  <ModalFooter>
                    <Button variant="outline" mr={3} onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      colorScheme="blue"
                      onClick={() => handleAddVacationDays(selectedVacationDays)}
                      isLoading={saving}
                    >
                      Save Changes
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Flex justify="flex-end" mt={8}>
        <Button
          colorScheme="blue"
          size="lg"
          onClick={handleSaveProfile}
          isLoading={saving}
        >
          Save Settings
        </Button>
      </Flex>
    </AppLayout>
  );
};

export default SettingsPage;